import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLoginMutation, useTransferAffiliateAccountMutation } from '../queries';
import { type AffiliateLoginFormValues, affiliateLoginSchema } from '../validations';
import { useAuthStore } from '@/stores/authStore';
import { fetchUser } from '@/lib/api/user';

interface UseAffiliateLoginFormProps {
    /** JWT token for API authorization */
    token: string;
    /** Callback when login and transfer succeeds */
    onSuccess: () => void;
}

/**
 * Hook to handle affiliate login form logic.
 * Logs in existing Stan user and transfers affiliate link to their account.
 */
export function useAffiliateLoginForm({ token, onSuccess }: UseAffiliateLoginFormProps) {
    const { mutateAsync: login, isPending: isLoggingIn } = useLoginMutation();
    const { mutateAsync: transferAccount, isPending: isTransferring } = useTransferAffiliateAccountMutation();
    const saveToken = useAuthStore(state => state.saveToken);
    const [loginError, setLoginError] = useState<string | undefined>();

    const form = useForm<AffiliateLoginFormValues>({
        resolver: zodResolver(affiliateLoginSchema),
        defaultValues: {
            emailOrUsername: '',
            password: '',
        },
        mode: 'onChange',
    });

    const onSubmit = form.handleSubmit(async data => {
        setLoginError(undefined);

        try {
            // Step 1: Login with credentials
            const loginResponse = await login({
                email_or_username: data.emailOrUsername,
                password: data.password,
            });

            if (!loginResponse.token) {
                setLoginError('Please enter valid email address and password.');
                return;
            }

            // Save token temporarily
            saveToken(loginResponse.token);

            // Step 2: Fetch user data to get email and username
            const user = await fetchUser(loginResponse.token);

            if (!user.email || !user.username) {
                setLoginError('Please enter valid email address and password.');
                return;
            }

            // Step 3: Transfer affiliate account to this user
            await transferAccount({
                payload: { email: user.email, username: user.username },
                token,
            });

            onSuccess();
        } catch {
            setLoginError('Please enter valid email address and password.');
        }
    });

    return {
        form,
        onSubmit,
        isSubmitting: isLoggingIn || isTransferring,
        loginError,
    };
}
